import { z } from 'zod'

const JsonValueSchema = z.json()
const TimestampSchema = z.iso.datetime({ offset: true })

export const StorefrontTargetSchema = z.enum(['ymarket', 'wildberries', 'ozon', 'ori_site'])
export const StorefrontEnvironmentSchema = z.enum(['production', 'sandbox'])
export const SellerArticlePolicySchema = z.enum(['article_size', 'uin', 'uin_then_sync_identifier', 'sync_identifier_only'])

const StorefrontCredentialStatusSchema = z.object({
  state: z.enum(['configured', 'missing', 'not_required']),
  label: z.string(),
}).strict()

const StorefrontTargetSettingsSchema = z.object({
  sellerArticlePolicy: SellerArticlePolicySchema,
  cardPublishingEnabled: z.boolean(),
  credentialStatus: StorefrontCredentialStatusSchema,
  environment: StorefrontEnvironmentSchema,
}).strict()

export const PackageProductTypeSchema = z.enum(['ring', 'earrings', 'pendant', 'chain', 'bracelet', 'necklace', 'brooch', 'piercing', 'wedding_ring', 'cufflinks'])

const YmPackageDefaultSchema = z.object({
  productType: PackageProductTypeSchema,
  lengthCm: z.number().positive().max(300),
  widthCm: z.number().positive().max(300),
  heightCm: z.number().positive().max(300),
  weightKg: z.number().positive().max(50),
}).strict()

const YmPricingPolicySchema = z.object({
  discountBaseMarkupPercent: z.number().positive(),
  floorRubPerGram: z.number().positive(),
  targetRubPerGram: z.number().positive(),
  markupFloorPercent: z.number().positive(),
  markupTargetPercent: z.number().positive(),
  netTargetGoldRubPerGram: z.number().positive().default(15000),
  netTargetSilverRubPerGram: z.number().positive().nullable().default(null),
  includeStonePieces: z.boolean().default(false),
  packageDefaults: z.array(YmPackageDefaultSchema).optional().default([]),
}).strict().refine(
  (policy) => policy.targetRubPerGram >= policy.floorRubPerGram && policy.markupTargetPercent >= policy.markupFloorPercent,
  { path: ['targetRubPerGram'], message: 'YM target must not be below floor' },
)

export const StorefrontSettingsSchema = z.object({
  sellerArticlePolicy: SellerArticlePolicySchema,
  ymarketCardPublishingEnabled: z.boolean(),
  wildberriesCardPublishingEnabled: z.boolean(),
  ozonCardPublishingEnabled: z.boolean(),
  oriSiteCardPublishingEnabled: z.boolean(),
  updatedBy: z.string(),
  updatedAt: TimestampSchema.nullable().optional().default(null),
  ymarketPricingPolicy: YmPricingPolicySchema.optional().default({
    discountBaseMarkupPercent: 10,
    floorRubPerGram: 12000,
    targetRubPerGram: 18000,
    markupFloorPercent: 80,
    markupTargetPercent: 100,
    netTargetGoldRubPerGram: 15000,
    netTargetSilverRubPerGram: null,
    includeStonePieces: false,
    packageDefaults: [],
  }),
  ymarketPricingPolicyAudit: z.array(z.object({
    changedBy: z.string(),
    changedAt: TimestampSchema,
    old: z.record(z.string(), JsonValueSchema),
    new: z.record(z.string(), JsonValueSchema),
  }).strict()).optional().default([]),
  credentialStatus: z.record(StorefrontTargetSchema, StorefrontCredentialStatusSchema),
  targets: z.record(StorefrontTargetSchema, StorefrontTargetSettingsSchema),
}).strict()

export const BatchRefusalSchema = z.object({
  code: z.string(),
  label: z.string(),
  detail: z.string().nullable().optional(),
}).strict()

export const IdentifierExampleSchema = z.object({
  identifier: z.string().nullable(),
  source: z.string().nullable(),
  refusal: BatchRefusalSchema.nullable(),
}).strict()

export const BatchPayloadSchema = z.object({
  target: StorefrontTargetSchema,
  method: z.string(),
  path: z.string(),
  body: JsonValueSchema,
  payloadHash: z.string(),
  productIds: z.array(z.string().uuid()),
  groupingKey: z.string().nullable(),
  ungroupedReason: z.string().nullable(),
}).strict()

export const BatchItemPreviewSchema = z.object({
  productId: z.string().uuid(),
  target: StorefrontTargetSchema,
  sellerArticle: z.string().nullable(),
  sellerArticleSource: z.string().nullable(),
  group: z.string().nullable(),
  photoCount: z.number().int().nonnegative(),
  videoCount: z.number().int().nonnegative(),
  refusals: z.array(BatchRefusalSchema),
  payload: BatchPayloadSchema.nullable(),
}).strict()

export const StorefrontBatchPreviewSchema = z.object({
  productIds: z.array(z.string().uuid()),
  targets: z.array(StorefrontTargetSchema),
  batchHash: z.string().length(64),
  items: z.array(BatchItemPreviewSchema),
}).strict()

export const StorefrontBatchApplySchema = z.object({ batchId: z.string().uuid() }).strict()

export const PublicationProgressStageSchema = z.enum([
  'queued',
  'awaiting_nm_id',
  'media_readiness',
  'media_submitted',
  'media_verification',
  'repair',
  'complete',
  'blocked',
  'unknown',
])

export const StorefrontBatchItemSchema = z.object({
  productId: z.string().uuid(),
  target: StorefrontTargetSchema,
  payloadHash: z.string().length(64).nullable(),
  state: z.enum(['queued', 'publishing', 'published', 'rejected']),
  refusalReason: z.string().nullable(),
  refusalMessage: z.string().nullable(),
  providerId: z.string().nullable(),
  updatedAt: TimestampSchema,
  stage: PublicationProgressStageSchema,
  completed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  lastUpdate: TimestampSchema,
  failureLocation: z.string().nullable(),
}).strict()

export const StorefrontBatchStatusSchema = z.object({
  batchId: z.string().uuid(),
  batchHash: z.string().length(64),
  state: z.enum(['queued', 'running', 'completed', 'failed']),
  stage: PublicationProgressStageSchema,
  completed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  lastUpdate: TimestampSchema,
  failureLocation: z.string().nullable(),
  items: z.array(StorefrontBatchItemSchema),
}).strict()

export const MediaRepairMediaSchema = z.object({
  kind: z.string(),
  url: z.string().url(),
  locator: z.string().nullable(),
  position: z.number().int().positive().nullable(),
  mediaType: z.string().nullable(),
  byteSize: z.number().int().positive().nullable(),
  widthPx: z.number().int().positive().nullable(),
  heightPx: z.number().int().positive().nullable(),
  durationSeconds: z.number().nonnegative().nullable(),
  isAnimated: z.boolean().nullable(),
}).strict()

export const MediaIssueCodeSchema = z.enum([
  'media.download_failed',
  'media.download_pending',
  'media.probe_unavailable',
  'wildberries.photo_dimensions',
  'wildberries.photo_ratio',
  'wildberries.photo_format',
  'wildberries.photo_size',
  'wildberries.photo_limit',
  'wildberries.video_limit',
  'wildberries.video_unavailable',
  'wildberries.publication_blocked',
])

export const MediaIssueScopeSchema = z.enum(['generic', 'wildberries', 'fixable', 'non_fatal'])

export const MediaRepairIssueSchema = z.object({
  code: MediaIssueCodeSchema,
  label: z.string(),
  scope: MediaIssueScopeSchema,
  sourceEvidence: z.string().nullable(),
  actionable: z.boolean(),
  unavailable: z.boolean(),
  detail: z.string().nullable(),
  kind: z.string().nullable(),
  position: z.number().int().positive().nullable(),
}).strict()

export const MediaRepairProjectionSchema = z.object({
  kind: z.string(),
  sourceEvidence: z.string().nullable(),
  position: z.number().int().positive().nullable(),
  state: z.enum(['included', 'unavailable', 'excess_video']),
  reason: MediaIssueCodeSchema.nullable(),
}).strict()

export const MediaRepairPreviewSchema = z.object({
  previewHash: z.string().length(64),
  target: z.literal('wildberries'),
  productId: z.string().uuid(),
  providerAccountId: z.string().uuid(),
  providerId: z.string(),
  sellerArticle: z.string(),
  media: z.array(MediaRepairMediaSchema),
  issues: z.array(MediaRepairIssueSchema),
  projection: z.array(MediaRepairProjectionSchema),
  predecessorBatchId: z.string().uuid().nullable(),
  remotePhotoCount: z.number().int().nonnegative().nullable(),
  remoteVideoPresent: z.boolean().nullable(),
  remoteObservedAt: TimestampSchema,
  replacementMode: z.literal('full'),
}).strict()

export const MediaRepairApplySchema = z.object({
  target: z.literal('wildberries'),
  payloadHash: z.string().length(64),
  providerId: z.string().nullable(),
  submittedAt: TimestampSchema,
  actor: z.string(),
  mediaState: z.enum(['not_required', 'accepted', 'verified', 'unknown', 'failed']),
  mediaReason: z.string().nullable(),
}).strict()

export const PublicationSchema = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid(),
  target: StorefrontTargetSchema,
  state: z.string(),
  targetSellerArticle: z.string().nullable(),
  observedSellerArticle: z.string().nullable(),
  publicationGroupId: z.string().uuid().nullable(),
  stateUnavailableReason: z.string().nullable(),
  stateChangeReason: z.string().nullable(),
  lastUpdate: TimestampSchema,
}).strict()

export const PublicationsSchema = z.object({ items: z.array(PublicationSchema) }).strict()

export type StorefrontTarget = z.infer<typeof StorefrontTargetSchema>
export type StorefrontEnvironment = z.infer<typeof StorefrontEnvironmentSchema>
export type SellerArticlePolicy = z.infer<typeof SellerArticlePolicySchema>
export type StorefrontSettings = z.infer<typeof StorefrontSettingsSchema>
export type StorefrontTargetSettings = z.infer<typeof StorefrontTargetSettingsSchema>
export type BatchRefusal = z.infer<typeof BatchRefusalSchema>
export type BatchItemPreview = z.infer<typeof BatchItemPreviewSchema>
export type StorefrontBatchPreview = z.infer<typeof StorefrontBatchPreviewSchema>
export type StorefrontBatchStatus = z.infer<typeof StorefrontBatchStatusSchema>
export type MediaRepairPreview = z.infer<typeof MediaRepairPreviewSchema>
export type MediaRepairApply = z.infer<typeof MediaRepairApplySchema>
export type Publication = z.infer<typeof PublicationSchema>
